import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Flex, HStack, VStack } from "@chakra-ui/layout";
import { Text, useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import LoadingIndicator from "../components/Loading";
import LeftArrow from "../components/icons/LeftArrow";
import {
	useJobSchedule,
	useOrganisation,
	useScheduleActions,
	useScheduleJobApplications,
	useScheduleJobDetails,
} from "@api/hooks";
import { fetchJobMatchingConsultants } from "@api/hooks/useSchedule";
import WeekSchedule, { WeekScheduleHandle } from "../components/scheduleView/week/WeekSchedule";
import { getConsultantName } from "../components/scheduleView/week/ApplicantList";
import { dateKey, getExtensionConsultant, WeekScheduleDraft } from "../components/scheduleView/week/weekSchedule.utils";
import { getCompetenceLabelByKey, getServiceName } from "@utils/utils";
import { cn } from "@/lib/utils";

/** Weeks shown after the job's last shift (or the current week) at first */
const RANGE_PAD_AFTER = 8;
/** Weeks added at a time when scrolling close to the first or last week */
const RANGE_EXTEND_BY = 8;
/** Scrolling back stops two months before the current week (or at the job's
 * first week), forward after two years like the multi-job schedule */
const RANGE_FLOOR_WEEKS = 9;
const RANGE_CAP_WEEKS = 104;

const EDITABLE_JOB_STATUSES = ["OPEN", "BOOKED_WITH_UNMANNED_SHIFTS", "BOOKED"];

const weekOf = (date: string | moment.Moment) => dateKey(moment(date).startOf("isoWeek"));
const currentWeekPlus = (weeks: number) => weekOf(moment().add(weeks, "week"));
const minWeek = (a: string, b: string) => (a < b ? a : b);
const maxWeek = (a: string, b: string) => (a > b ? a : b);

const navButtonClass =
	"flex h-11 cursor-pointer items-center justify-center rounded-[14px] border border-solid border-[#B5B4B2] !bg-white !px-3 text-sm font-medium text-gray-800 hover:!bg-gray-50";

/**
 * One job's schedule in the week grid. Clicking an empty day adds a draft
 * shift; the drafts are sent in one request, which for a job with a booked
 * consultant is an extension request to that consultant (like the old
 * "Förläng uppdrag" page, which is kept at /uppdrag/:id/tid).
 */
const JobSchedulePage = () => {
	const { id: jobId = "" } = useParams();
	const navigate = useNavigate();
	const toast = useToast();
	const queryClient = useQueryClient();
	const { organisation } = useOrganisation();
	const scheduleRef = useRef<WeekScheduleHandle>(null);
	const [drafts, setDrafts] = useState<WeekScheduleDraft[]>([]);

	const jobIds = useMemo(() => (jobId ? [jobId] : []), [jobId]);
	const detailsByJobId = useScheduleJobDetails(jobIds);
	const applicationsByJobId = useScheduleJobApplications(jobIds);
	const details = detailsByJobId[jobId];
	const startDate = details?.startDate;
	const endDate = details?.endDate;
	const customerId = details?.customer.id;

	// The schedule endpoint has the shape the grid wants (who works which shift,
	// absences), so the job is read from it over the job's own dates
	const scheduleRanges = useMemo(
		() =>
			startDate && endDate
				? [{ startTime: moment(startDate).startOf("day").format(), endTime: moment(endDate).endOf("day").format() }]
				: [],
		[startDate, endDate],
	);
	const customerIds = useMemo(() => (customerId ? [customerId] : []), [customerId]);
	const { jobSchedules, loadedRanges } = useJobSchedule(
		scheduleRanges,
		customerIds,
		["customer", "applications.consultant"],
		["applications.consultant.absences", "applications.consultant.hasWorked"],
	);
	const jobs = useMemo(() => jobSchedules.filter((job) => job.id === jobId), [jobSchedules, jobId]);
	const job = jobs[0];
	const isLoaded = !!details && loadedRanges.length === scheduleRanges.length;

	const firstShiftWeek = startDate && weekOf(startDate);
	const lastShiftWeek = endDate && weekOf(endDate);
	const currentWeek = currentWeekPlus(0);
	// Weeks added at either end by scrolling; the job's own weeks come from its dates
	const [extraWeeks, setExtraWeeks] = useState({ before: 0, after: 0 });
	const range = useMemo(() => {
		if (!firstShiftWeek || !lastShiftWeek) return undefined;
		const floor = minWeek(firstShiftWeek, currentWeekPlus(-RANGE_FLOOR_WEEKS));
		const cap = currentWeekPlus(RANGE_CAP_WEEKS);
		const start = weekOf(moment(minWeek(firstShiftWeek, currentWeekPlus(-1))).subtract(extraWeeks.before, "week"));
		const end = weekOf(moment(maxWeek(lastShiftWeek, currentWeek)).add(RANGE_PAD_AFTER + extraWeeks.after, "week"));
		return { start: maxWeek(start, floor), end: minWeek(end, cap) };
	}, [firstShiftWeek, lastShiftWeek, currentWeek, extraWeeks]);

	const loadEarlierWeeks = () =>
		setExtraWeeks((prev) =>
			range && range.start > minWeek(firstShiftWeek || range.start, currentWeekPlus(-RANGE_FLOOR_WEEKS))
				? { ...prev, before: prev.before + RANGE_EXTEND_BY }
				: prev,
		);
	const loadLaterWeeks = () =>
		setExtraWeeks((prev) =>
			range && range.end < currentWeekPlus(RANGE_CAP_WEEKS) ? { ...prev, after: prev.after + RANGE_EXTEND_BY } : prev,
		);

	// Extending happens after the last shift, so the view opens there when it lies ahead
	const focusWeek = lastShiftWeek && lastShiftWeek > currentWeek ? lastShiftWeek : currentWeek;

	const competenceLabelByKey = useMemo(
		() => getCompetenceLabelByKey(organisation?.competences ?? []),
		[organisation?.competences],
	);

	const { updateShifts, inviteConsultants, bookApplication, isSaving, isInviting } = useScheduleActions(
		(message) => toast({ title: message, status: "success", duration: 5000, isClosable: true, variant: "subtle" }),
		(errorCode) =>
			toast({
				title: "Det gick inte att spara ändringen",
				description: errorCode,
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			}),
	);

	// Drafts only live on this page, so closing the tab with unsent ones is confirmed
	useEffect(() => {
		if (!drafts.length) return;
		const warn = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [drafts.length]);

	const confirmLeave = () =>
		!drafts.length ||
		window.confirm(
			`Du har ${drafts.length === 1 ? "ett pass" : `${drafts.length} pass`} som inte har skickats. Vill du lämna sidan ändå?`,
		);

	const goBack = () => {
		if (confirmLeave()) navigate(`/uppdrag/${jobId}`);
	};

	const isEditable = !!details && EDITABLE_JOB_STATUSES.includes(details.status);
	const isExtension = details?.status === "BOOKED";
	const extensionConsultant = job && getExtensionConsultant(job);
	const title = isExtension ? "Förläng uppdrag" : "Schema & pass";
	const serviceName = details && getServiceName(details.competence, organisation?.competences);

	return (
		<LoggedInLayout width="full" bg={"#edeff3"}>
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full" pb={drafts.length ? 28 : 10}>
				<Flex w="full" justify="space-between" alignItems="center" mt={2} gap={4} flexWrap="wrap">
					<VStack alignItems="start" spacing={0}>
						<Text fontWeight={600} fontSize={24} m={0}>
							{title}
						</Text>
						{details && (
							<Text fontWeight={400} fontSize={14} color={"#35353580"} m={0}>
								#{details.orderNumber}: {details.customer.name}
								{serviceName && `, ${serviceName}`}
							</Text>
						)}
					</VStack>
					<HStack gap={3}>
						<button type="button" className={navButtonClass} onClick={goBack}>
							<LeftArrow />
							<span className="ml-2">Tillbaka</span>
						</button>
						<button
							type="button"
							title="Visa denna vecka"
							className={navButtonClass}
							onClick={() => scheduleRef.current?.scrollToToday()}
						>
							Idag
						</button>
						{isSaving && <LoadingIndicator size={"s"} />}
					</HStack>
				</Flex>

				{isLoaded && isEditable && (
					<Text fontSize="sm" color="gray.600" m={0}>
						{extensionConsultant
							? `Klicka på en tom dag för att lägga till pass. Passen skickas som en förfrågan till ${getConsultantName(
									extensionConsultant,
								)}, som bokas på dem när hen tackat ja.`
							: "Klicka på en tom dag för att lägga till pass. Passen läggs till som obemannade och kan sedan bemannas."}
					</Text>
				)}
				{isLoaded && !isEditable && (
					<Text fontSize="sm" color="gray.600" m={0}>
						Uppdraget kan inte längre ändras.
					</Text>
				)}

				{!isLoaded && <LoadingIndicator size={"s"} />}
				{isLoaded && !job && (
					<Text fontSize="sm" color="gray.600" m={0}>
						Uppdraget har inga pass att visa.{" "}
						<Link to={`/uppdrag/${jobId}`} className="underline">
							Till uppdraget
						</Link>
					</Text>
				)}
				{isLoaded && job && range && (
					<WeekSchedule
						ref={scheduleRef}
						jobs={jobs}
						detailsByJobId={detailsByJobId}
						applicationsByJobId={applicationsByJobId}
						competenceLabelByKey={competenceLabelByKey}
						startWeek={range.start}
						endWeek={range.end}
						focusWeek={focusWeek}
						onReachStart={loadEarlierWeeks}
						onReachEnd={loadLaterWeeks}
						loadConsultants={(id) => fetchJobMatchingConsultants(queryClient, id)}
						onUpdateShifts={(id, message, change) => updateShifts({ jobId: id, message, change })}
						onInvite={(id, consultantIds, allowLaterBooking) => inviteConsultants({ jobId: id, consultantIds, allowLaterBooking })}
						isInviting={isInviting}
						onBook={(id, applicationId) => bookApplication({ jobId: id, applicationId })}
						drafts={drafts}
						onDraftsChange={setDrafts}
						showJobScheduleLink={false}
					/>
				)}
			</VStack>

			{drafts.length > 0 && (
				<div className="fixed bottom-6 left-1/2 z-40 flex w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 items-center gap-4 rounded-2xl border border-solid border-gray-200 bg-white px-5 py-3 shadow-lg">
					<div className="min-w-0 flex-1">
						<div className="text-sm font-semibold text-gray-900">
							{drafts.length === 1 ? "1 nytt pass" : `${drafts.length} nya pass`}
						</div>
						<div className="truncate text-xs text-gray-500">
							{extensionConsultant
								? `Förfrågan skickas till ${getConsultantName(extensionConsultant)}`
								: "Passen läggs till som obemannade"}
						</div>
					</div>
					<button
						type="button"
						className={cn(navButtonClass, "h-10")}
						disabled={isSaving}
						onClick={() => {
							if (window.confirm("Vill du ta bort alla nya pass?")) setDrafts([]);
						}}
					>
						Ångra
					</button>
					<button
						type="button"
						className="flex h-10 cursor-pointer items-center justify-center rounded-[14px] !bg-[#2A2A2A] !px-4 text-sm font-medium !text-white hover:!bg-black disabled:cursor-not-allowed disabled:!bg-gray-400"
						disabled={isSaving}
						onClick={() => scheduleRef.current?.submitDrafts()}
					>
						{extensionConsultant ? "Sänd förfrågan" : "Lägg till pass"}
					</button>
				</div>
			)}
		</LoggedInLayout>
	);
};

export default JobSchedulePage;
